import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import AuthBrandPanel, { AuthBrandMark } from '../components/auth/AuthBrandPanel';
import GoogleSignInButton from '../components/auth/GoogleSignInButton';
import { Button, Input, Select } from '@copilot/ui';
import { Field } from '../components/common/Field';
import { LANGUAGES } from '../constants/languages';
import { useAuth } from '../hooks/useAuth';
import { getAuthenticatedHomePath } from '../utils/authRedirects';
import { getUserFriendlyError } from '../utils/errors';

// Registration enrols every new account onto the zero-price plan in the
// same transaction (backend services/auth_service.py). When no such plan
// is configured the backend refuses with 503 + this code, which is a
// DEPLOYMENT problem the visitor can do nothing about - so it gets its
// own copy rather than the backend's admin-facing wording or the
// generic "unable to create account" fallback.
const FREE_PLAN_NOT_CONFIGURED = 'FREE_PLAN_NOT_CONFIGURED';
const FREE_PLAN_NOT_CONFIGURED_MESSAGE =
  'Registration is temporarily unavailable because the Free plan is not ' +
  'configured. Please try again later.';

function RegisterPage() {
  const { register, loginWithGoogle } = useAuth();
  const navigate = useNavigate();

  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [preferredLanguage, setPreferredLanguage] = useState('en');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Google does not distinguish signing up from signing in - the same
  // endpoint creates the account if it does not exist yet - so this is
  // the same handler the login page uses, differing only in the button
  // wording above it.
  const handleGoogleCredential = async (credential) => {
    setError('');

    try {
      // Routed by ROLE, not to a fixed path. Unlike password
      // registration below - which always produces a new, non-admin
      // account - this button also signs IN an existing user, and an
      // admin who reaches for it must still land on the admin home.
      const me = await loginWithGoogle(credential);
      navigate(getAuthenticatedHomePath(me), { replace: true });
    } catch (err) {
      setError(
        err.code === FREE_PLAN_NOT_CONFIGURED
          ? FREE_PLAN_NOT_CONFIGURED_MESSAGE
          : getUserFriendlyError(
              err,
              'Unable to connect. Please try again.',
              'Google Sign-In failed. Please try again.'
            )
      );
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setIsSubmitting(true);

    try {
      // Registration no longer signs anyone in - it sends a code. The
      // session begins on the verification screen.
      await register({ fullName, email, password, preferredLanguage });
      navigate('/verify-email', { replace: true, state: { email } });
    } catch (err) {
      // Branches on the machine-readable code, never the human message -
      // that text is copy and may be reworded server-side. Every other
      // failure keeps its existing behavior.
      setError(
        err.code === FREE_PLAN_NOT_CONFIGURED
          ? FREE_PLAN_NOT_CONFIGURED_MESSAGE
          : getUserFriendlyError(
              err,
              'Unable to connect. Please try again.',
              'Unable to create account.'
            )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-shell">
        <AuthBrandPanel />

        <div className="auth-card">
          <AuthBrandMark className="mobile-only" />

          <section className="auth-form-panel" aria-labelledby="register-heading">
            <div className="auth-heading" id="register-heading">Create Account</div>
            <p className="auth-panel-subtitle">
              Join KrishvaQ today
            </p>

            <form className="auth-form" onSubmit={handleSubmit}>
              <Field label="Full Name" htmlFor="register-name">
                <Input
                  id="register-name"
                  autoComplete="name"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  required
                />
              </Field>

              <Field label="Email" htmlFor="register-email">
                <Input
                  id="register-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </Field>

              <Field label="Password" htmlFor="register-password">
                <Input revealable type="password"
                  id="register-password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </Field>

              <Field label="Confirm Password" htmlFor="register-confirm">
                <Input revealable type="password"
                  id="register-confirm"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                />
              </Field>

              <Field label="Preferred Language" htmlFor="register-language">
                <Select
                  id="register-language"
                  value={preferredLanguage}
                  onChange={(e) => setPreferredLanguage(e.target.value)}
                >
                  {LANGUAGES.map((lang) => (
                    <option key={lang.code} value={lang.code}>
                      {lang.label}
                    </option>
                  ))}
                </Select>
              </Field>

              {error && <div className="form-alert">{error}</div>}

              <Button type="submit" block disabled={isSubmitting}>
                {isSubmitting ? 'Creating account...' : 'Sign Up'}
              </Button>
            </form>

            <div className="auth-divider" role="separator">
              <span>or</span>
            </div>

            <GoogleSignInButton
              onCredential={handleGoogleCredential}
              text="signup_with"
            />

            {/* Stated before the account exists, not after. The full
                consent prompt still appears at first sign-in
                (components/legal/TermsConsentModal.jsx) - this is so
                nobody can say they were never shown the terms until
                they had already handed over their details. */}
            <p className="auth-legal-note">
              By creating an account you agree to our{' '}
              <Link to="/terms">Terms of Service</Link> and{' '}
              <Link to="/privacy">Privacy Policy</Link>.
            </p>
          </section>

          <div className="auth-footer-link">
            Already have an account? <Link to="/login">Sign In</Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterPage;
